<template>
  <div v-if="images.length" class="carousel">
    <!--main carousel-->
    <swiper-container ref="swiper-container" init="false" class="carousel__main">
      <swiper-slide
        v-for="(image, image_index) in images"
        :key="`${_uid}:main:${image_index}`"
        class="carousel__item"
      >
        <img class="carousel__image" :src="image_url(image)" alt="" />
      </swiper-slide>
    </swiper-container>

    <!--thumbs carousel-->
    <swiper-container
      v-if="images.length > 1"
      ref="swiper-thumbs"
      init="false"
      class="carousel__thumbs"
    >
      <swiper-slide
        v-for="(image, image_index) in images"
        :key="`${_uid}:thumbnail:${image_index}`"
        class="carousel__item"
        :class="{ active: image_index === current_slide_index }"
        @click="swiper_instance.swiper.slideTo(image_index)"
      >
        <img class="carousel__image" :src="image_thumbnail(image)" alt="" />
      </swiper-slide>
    </swiper-container>

    <!--navigation-->
    <template v-if="images.length > 1">
      <vb-icon
        class="carousel__arrow carousel__arrow--prev"
        :name="'angle_left'"
        :size="26"
        @click="swiper_instance.swiper.slidePrev()"
      />
      <vb-icon
        class="carousel__arrow carousel__arrow--next"
        :name="'angle_right'"
        :size="26"
        @click="swiper_instance.swiper.slideNext()"
      />
    </template>
  </div>
</template>

<script>
  import { image_url, image_thumbnail } from './logic';
  export default {
    name: 'carousel',
    props: {
      images: {
        type: Array,
        default: () => [],
      },
      // quante thumb per schermata, sotto e sopra i 992px
      thumbs_per_view: {
        type: Object,
        default: () => ({ base: 4, lg: 8 }),
      },
    },

    data() {
      const vm = this;
      return {
        current_slide_index: 0,
        swiper_thumbs_params: {
          eventsPrefix: 'swiper:',
          slidesPerView: vm.thumbs_per_view.base,
          breakpoints: {
            // da lg in poi
            992: {
              slidesPerView: vm.thumbs_per_view.lg,
            },
          },
        },
        swiper_params: {
          eventsPrefix: 'swiper:',
          slidesPerView: 1,
          on: {
            slideChange(event) {
              // con una sola immagine i thumbs non vengono renderizzati
              if (vm.swiper_thumbs) {
                vm.swiper_thumbs.swiper.slideTo(event.activeIndex);
              }
              vm.current_slide_index = event.activeIndex;
            },
          },
        },
      };
    },

    watch: {
      // gli swiper esistono solo con almeno un'immagine: se le immagini arrivano
      // dopo il mount vanno inizializzati quando gli elementi compaiono
      'images.length'() {
        this.$nextTick(this.initialize_swipers);
      },
    },

    mounted() {
      this.initialize_swipers();
    },

    methods: {
      image_url,
      image_thumbnail,

      initialize_swipers() {
        this.swiper_thumbs = this.initialize_swiper(
          'swiper-thumbs',
          this.swiper_thumbs,
          this.swiper_thumbs_params
        );
        this.swiper_instance = this.initialize_swiper(
          'swiper-container',
          this.swiper_instance,
          this.swiper_params
        );
      },

      initialize_swiper(ref_name, initialized_element, params) {
        const element = this.$refs[ref_name];

        if (!element) return null;
        if (element === initialized_element) return initialized_element;

        Object.assign(element, params);
        element.initialize();
        // Params are set, so auto-init is safe again (TAZ-629) and needed: a
        // DOM move destroys the instance and only connectedCallback rebuilds it.
        element.removeAttribute('init');
        return element;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .carousel {
    $this: &;
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 20px;

    &__main {
      overflow: hidden;
    }

    &__thumbs {
      margin-top: 6px;
      display: none;

      @include media-breakpoint-up(sm) {
        display: block;
      }
    }

    &__item {
      position: relative;

      @at-root #{$this}__main & {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 270px;
        border-radius: 10px;
        overflow: hidden;

        @include media-breakpoint-up(lg) {
          height: 400px;
        }
      }

      @at-root #{$this}__thumbs & {
        cursor: pointer;
        padding-right: 3px;
        padding-left: 3px;
        height: 100px;
        overflow: hidden;
        border-radius: 5px;

        @include media-breakpoint-up(md) {
          height: 90px;
        }

        &:after {
          position: absolute;
          top: 0;
          left: 3px;
          right: 3px;
          bottom: 0;
          transition: $transition-base;
          z-index: 1;
          border: 2px solid transparent;
          border-radius: 5px;
        }
      }

      &.active {
        &:after {
          content: '';
          border-color: var(--secondary);
        }
      }
    }

    &__image {
      object-fit: cover;
      object-position: center center;
      min-height: 100%;
      min-width: 100%;

      @at-root #{$this}__thumbs & {
        border-radius: 5px;

        &:hover {
          opacity: 0.6;
        }
      }

      @at-root #{$this}__item.active & {
        opacity: 0.4;
      }
    }

    &__arrow {
      position: absolute;
      top: 0;
      bottom: 0;
      z-index: 10;
      cursor: pointer;
      transition: $transition-base;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 10px;

      @include media-breakpoint-up(sm) {
        bottom: 95px;
      }

      &--prev {
        left: 0;

        ::v-deep {
          svg {
            filter: drop-shadow(-2px 0 0 $white);
          }
        }
      }
      &--next {
        right: 0;

        ::v-deep {
          svg {
            filter: drop-shadow(2px 0 0 $white);
          }
        }
      }

      &:hover {
        @include media-breakpoint-up(lg) {
          background-color: rgba($white, 0.5);
        }
      }
    }
  }
</style>
